import React from 'react';
import PropTypes from 'prop-types';
import './LoadingSkeleton.css';

/**
 * LoadingSkeleton Component
 * Displays animated loading placeholders
 *
 * @param {string} type - Type of skeleton: 'text', 'card', 'table', 'dashboard'
 * @param {number} rows - Number of rows for text/table type (default: 3)
 * @param {string} height - Custom height (e.g., '200px', '100%')
 * @param {string} width - Custom width (e.g., '100%', '300px')
 * @param {number} count - Number of skeleton items to render (default: 1)
 */
const LoadingSkeleton = ({
  type = 'text',
  rows = 3,
  height,
  width = '100%',
  count = 1
}) => {
  const renderSkeleton = () => {
    switch (type) {
      case 'text':
        return (
          <div className="skeleton-text-container">
            {Array.from({ length: rows }).map((_, index) => (
              <div
                key={index}
                className="skeleton-text"
                style={{
                  width: index === rows - 1 ? '60%' : width,
                  height: height || '16px'
                }}
              />
            ))}
          </div>
        );

      case 'card':
        return (
          <div className="skeleton-card" style={{ height: height || '200px' }}>
            <div className="skeleton-card__header">
              <div className="skeleton-circle" style={{ width: '40px', height: '40px' }} />
              <div className="skeleton-text-container" style={{ flex: 1 }}>
                <div className="skeleton-text" style={{ width: '60%', height: '16px' }} />
                <div className="skeleton-text" style={{ width: '40%', height: '14px' }} />
              </div>
            </div>
            <div className="skeleton-card__body">
              <div className="skeleton-text" style={{ width: '100%', height: '14px' }} />
              <div className="skeleton-text" style={{ width: '80%', height: '14px' }} />
              <div className="skeleton-text" style={{ width: '90%', height: '14px' }} />
            </div>
          </div>
        );

      case 'table':
        return (
          <div className="skeleton-table">
            <div className="skeleton-table__header">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="skeleton-text" style={{ height: '20px' }} />
              ))}
            </div>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <div key={rowIndex} className="skeleton-table__row">
                {Array.from({ length: 4 }).map((_, colIndex) => (
                  <div key={colIndex} className="skeleton-text" style={{ height: '16px' }} />
                ))}
              </div>
            ))}
          </div>
        );

      case 'dashboard':
        return (
          <div className="skeleton-dashboard">
            <div className="skeleton-dashboard__stats">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="skeleton-stat-card">
                  <div className="skeleton-circle" style={{ width: '48px', height: '48px' }} />
                  <div className="skeleton-text" style={{ width: '80%', height: '20px', marginTop: '12px' }} />
                  <div className="skeleton-text" style={{ width: '60%', height: '14px', marginTop: '8px' }} />
                </div>
              ))}
            </div>
            <div className="skeleton-dashboard__content">
              <div className="skeleton-card" style={{ height: '300px' }} />
            </div>
          </div>
        );

      default:
        return (
          <div
            className="skeleton-box"
            style={{ width, height: height || '40px' }}
          />
        );
    }
  };

  if (count === 1) {
    return renderSkeleton();
  }

  return (
    <div className="skeleton-multiple">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="skeleton-item">
          {renderSkeleton()}
        </div>
      ))}
    </div>
  );
};

LoadingSkeleton.propTypes = {
  type: PropTypes.oneOf(['text', 'card', 'table', 'dashboard', 'box']),
  rows: PropTypes.number,
  height: PropTypes.string,
  width: PropTypes.string,
  count: PropTypes.number,
};

export default LoadingSkeleton;
